import type { PendingMutation } from "./pendingMutationsDb.ts";

// Past this age an entry is not sent again: a write that old could overwrite something newer.
export const RESEND_WITHIN_MS = 24 * 60 * 60 * 1000;
export const KEEP_FAILED_FOR_MS = 30 * 24 * 60 * 60 * 1000;

export function isTooOldToResend(entry: PendingMutation, now: number): boolean {
  return entry.createdAt < now - RESEND_WITHIN_MS;
}

export function isPrunableFailure(entry: PendingMutation, now: number): boolean {
  return entry.status === "failed" && entry.createdAt < now - KEEP_FAILED_FOR_MS;
}

// Sending this one while an older one for the same endpoint is still unsent or in flight would let
// the older value win once it finally lands. Other endpoints are a different resource, and a newer
// entry is not something this one has to wait for.
export function hasOlderUnsentFor(
  unfinished: PendingMutation[],
  entry: Pick<PendingMutation, "operationUuid" | "endpoint" | "createdAt">,
): boolean {
  return unfinished.some(
    (other) =>
      other.operationUuid !== entry.operationUuid &&
      other.endpoint === entry.endpoint &&
      other.createdAt < entry.createdAt,
  );
}

// A request carries no identity of its own — the server writes it as whoever is logged in when it
// arrives. An entry left by another session must not be sent; an unattributed one cannot be sent
// either, but deleting it would destroy the only copy silently, so it is failed and stays visible.
export function partitionByOwner(
  entries: PendingMutation[],
  userUuid: string,
): { foreign: PendingMutation[]; unattributed: PendingMutation[] } {
  const notOurs = entries.filter(
    (entry) => entry.userUuid !== userUuid && entry.status !== "failed",
  );
  return {
    foreign: notOurs.filter((entry) => entry.userUuid !== undefined),
    unattributed: notOurs.filter((entry) => entry.userUuid === undefined),
  };
}

// Only an entry we already tried to send can be in the server's history. Requeuing resets the
// status, so the attempt has to be recorded rather than inferred from it.
export function selectSentUnresolved(
  entries: PendingMutation[],
  userUuid: string,
): PendingMutation[] {
  return entries.filter(
    (entry) => entry.status !== "failed" && entry.userUuid === userUuid && entry.sent === true,
  );
}
